import type { MessageClass, ToolPart } from '@cotera/sdk/core';

/**
 * For each artifact id, the tool call id of the last message part that produced it.
 * Matches {@link Conversation.artifacts} dedupe (later tool output wins).
 */
export function collectLatestArtifactToolCallIds(
  messages: readonly MessageClass[]
): ReadonlyMap<string, string> {
  const latest = new Map<string, string>();
  for (const message of messages) {
    for (const part of message.parts) {
      if (!part.isToolPart()) {
        continue;
      }
      const artifact = part.getArtifact();
      if (artifact === null) {
        continue;
      }
      const artifactId =
        artifact.artifactId !== undefined && artifact.artifactId !== ''
          ? artifact.artifactId
          : part.toolCallId;
      latest.set(artifactId, part.toolCallId);
    }
  }
  return latest;
}

/** True when this tool part should render its inline artifact snippet in chat. */
export function shouldShowInlineArtifactSnippet(
  part: ToolPart,
  latestByArtifactId: ReadonlyMap<string, string>
): boolean {
  const artifact = part.getArtifact();
  if (artifact === null) {
    return false;
  }
  const artifactId =
    artifact.artifactId !== undefined && artifact.artifactId !== ''
      ? artifact.artifactId
      : part.toolCallId;
  return latestByArtifactId.get(artifactId) === part.toolCallId;
}

/** Whether this tool part is a superseded artifact update (not the latest). */
export function isSupersededArtifactToolCall(
  part: ToolPart,
  latestByArtifactId: ReadonlyMap<string, string>
): boolean {
  const artifact = part.getArtifact();
  if (artifact === null) {
    return false;
  }
  return !shouldShowInlineArtifactSnippet(part, latestByArtifactId);
}

export type ArtifactInlineDisplayMode = 'full' | 'edit' | 'none';

export function getArtifactInlineDisplayMode(
  part: ToolPart,
  latestByArtifactId: ReadonlyMap<string, string> | null
): ArtifactInlineDisplayMode {
  const artifact = part.getArtifact();
  if (artifact === null) {
    return 'none';
  }
  if (latestByArtifactId === null) {
    return 'full';
  }
  return shouldShowInlineArtifactSnippet(part, latestByArtifactId)
    ? 'full'
    : 'edit';
}
